import { type FocusEvent, useState } from "react";

/** True when focus fell back to the body, as it does when the focused element unmounts. */
export const isFocusLost = () => {
  const active = document.activeElement;
  return !active || active === document.body;
};

/** Marks a root entry, so focus can return to it after a neighbor is hidden. */
export const navEntrySelector = (key: string) =>
  `[data-nav-entry="${CSS.escape(key)}"]`;
export const NAV_MORE_SELECTOR = "[data-nav-more]";
/** The product nav's list of rows, above its footer. */
export const NAV_LIST_SELECTOR = "[data-nav-list]";
export const NAV_TOGGLE_SELECTOR = "[data-nav-toggle]";

/**
 * Focuses the element a selector names, or the first link or button inside it, after the next paint.
 * With `onlyIfLost`, it leaves focus alone unless it fell to the body.
 */
export const focusNavTarget = (
  selector: string,
  { onlyIfLost = false }: { onlyIfLost?: boolean } = {},
) => {
  const focus = () => {
    if (onlyIfLost && !isFocusLost()) return;
    const target = document.querySelector<HTMLElement>(selector);
    const focusable = target?.matches("a, button")
      ? target
      : target?.querySelector<HTMLElement>("a, button");
    if (focusable) focusQuietly(focusable, { preventScroll: false });
  };
  requestAnimationFrame(focus);
};

let quietFocusTarget: Element | null = null;

/** Focuses an element for the viewer rather than at their request, so a tooltip on it stays shut. */
export const focusQuietly = (element: HTMLElement, options?: FocusOptions) => {
  quietFocusTarget = element;
  try {
    element.focus(options);
  } finally {
    quietFocusTarget = null;
  }
};

/** Whether a control's tooltip stays shut: focus came from `focusQuietly`, not from the viewer, and hasn't left since. */
export const useIsQuietlyFocused = () => {
  const [isQuiet, setIsQuiet] = useState(false);
  return {
    isQuiet,
    onFocus: (event: FocusEvent<HTMLElement>) =>
      setIsQuiet(quietFocusTarget === event.currentTarget),
    onBlur: () => setIsQuiet(false),
  };
};
